import React, { ReactNode } from 'react'
import { Box, CircularProgress, Typography, Button, Paper } from '@mui/material'
import { Lock as LockIcon, Error as ErrorIcon } from '@mui/icons-material'
import { useOktaAuth } from '../../contexts/OktaAuthContext'
import config from '../../config'

interface ProtectedRouteProps {
  children: ReactNode
  fallback?: ReactNode
}

const LoadingScreen: React.FC = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    gap={2}
  >
    <CircularProgress size={60} />
    <Typography variant="h6" color="textSecondary">
      Authenticating...
    </Typography>
  </Box>
)

const LoginScreen: React.FC<{ onLogin: () => void }> = ({ onLogin }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    sx={{ backgroundColor: 'background.default', p: 3 }}
  >
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 400,
        width: '100%',
        textAlign: 'center',
        borderRadius: 2
      }}
    >
      <Box mb={3}>
        <LockIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom>
          {config.app.name}
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Please sign in to access the application
        </Typography>
      </Box>
      
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={onLogin}
        sx={{ py: 1.5, fontSize: '1.1rem' }}
      >
        Sign In with Okta
      </Button>
      
      <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
        Secure authentication powered by Okta
      </Typography>
    </Paper>
  </Box>
)

const ErrorScreen: React.FC<{ error: string; onRetry?: () => void }> = ({ error, onRetry }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
    sx={{ backgroundColor: 'background.default', p: 3 }}
  >
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        borderRadius: 2
      }}
    >
      <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
      <Typography variant="h5" component="h1" gutterBottom color="error">
        Authentication Error
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        {error}
      </Typography>
      
      {onRetry && (
        <Button
          variant="outlined"
          onClick={onRetry}
          sx={{ mt: 2 }}
        >
          Try Again
        </Button>
      )}
      
      <Typography variant="caption" color="textSecondary" sx={{ mt: 3, display: 'block' }}>
        If this problem persists, please contact your administrator
      </Typography>
    </Paper>
  </Box>
)

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback 
}) => {
  const { isAuthenticated, isLoading, login, authState } = useOktaAuth()

  // If Okta is disabled, render children directly
  if (!config.okta.enabled) {
    return <>{children}</>
  }

  // Show loading screen while authentication state is being determined
  if (isLoading) {
    return fallback || <LoadingScreen />
  }

  // Handle authentication errors
  if (authState?.error) {
    const errorMessage = authState.error.message || 'An authentication error occurred'
    return <ErrorScreen error={errorMessage} onRetry={login} />
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} />
  }

  // User is authenticated, render the protected content
  return <>{children}</>
}

export default ProtectedRoute
